"use client";

import { motion } from "motion/react";
import {
  TreePine,
  Flower2,
  Bike,
  Sprout,
  Trophy,
  Castle,
  Dumbbell,
  Users,
  Activity,
  Waves,
  Trees,
  Laptop,
  type LucideIcon
} from "lucide-react";
import type { Amenity } from "@/types/database";

// Map icon names to Lucide components
const iconMap: Record<string, LucideIcon> = {
  TreePine,
  Flower2,
  Bike,
  Sprout,
  Trophy,
  Castle,
  Dumbbell,
  Users,
  Activity,
  Waves,
  Trees,
  Laptop,
};

// Default amenities for fallback
const defaultAmenities = [
  { icon: TreePine, label: "Adventure Walk" },
  { icon: Flower2, label: "Yoga Zone & Meditation Deck" },
  { icon: Bike, label: "Cycling Networks" },
  { icon: Sprout, label: "Community Garden" },
  { icon: Trophy, label: "Sports Court" },
  { icon: Castle, label: "Massive Clubhouse" },
  { icon: Dumbbell, label: "Fitness & Wellness Decks" },
  { icon: Users, label: "Community Pavilion" },
  { icon: Activity, label: "Jogging Loop" },
  { icon: Waves, label: "Beach Lagoon" },
  { icon: Trees, label: "Forest Play Park" },
  { icon: Laptop, label: "Co-working Space" },
];

interface PropertyAmenitiesProps {
  amenities?: Amenity[];
}

export function PropertyAmenities({ amenities }: PropertyAmenitiesProps) {
  // Convert database amenities to display format, or use defaults
  const displayAmenities = amenities && amenities.length > 0
    ? amenities.map(a => ({
      icon: iconMap[a.icon] || TreePine,
      label: a.name,
    }))
    : defaultAmenities;
  return (
    <section className="py-24 px-6 md:px-12 max-w-[1400px] mx-auto bg-background">
      <div className="text-center mb-16 space-y-4">
        <div className="space-y-4">
          <span className="text-xs font-bold tracking-[0.3em] text-secondary uppercase block">
            World-Class Amenities
          </span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-serif text-3xl md:text-5xl text-primary"
          >
            Expansive Living, Shaped by <span className="italic text-primary/50">Nature</span>
          </motion.h2>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
        {displayAmenities.map((item, index) => {
          const IconComponent = item.icon;
          return (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="flex flex-col items-center text-center p-8 bg-secondary/10 rounded-xl hover:bg-secondary/20 transition-colors"
            >
              <IconComponent className="w-12 h-12 text-primary/60 mb-6 stroke-[1]" />
              <span className="text-xs md:text-sm uppercase tracking-widest font-medium text-foreground/80">
                {item.label}
              </span>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
